import _ from "lodash";

export function formatNumberScientific(num: number, digits: number) {
  return num.toExponential(digits);
  // const lookup = [
  //   { value: 1e-15, symbol: "×10⁻¹⁵" },
  //   { value: 1e-12, symbol: "×10⁻¹²" },
  //   { value: 1e-9, symbol: "×10⁻⁹" },
  //   { value: 1e-6, symbol: "×10⁻⁶" },
  //   { value: 1e-3, symbol: "×10⁻³" },
  //   { value: 1, symbol: "" },
  //   { value: 1e3, symbol: "×10³" },
  //   { value: 1e6, symbol: "×10⁶" },
  //   { value: 1e9, symbol: "×10⁹" },
  //   { value: 1e12, symbol: "×10¹²" },
  //   { value: 1e15, symbol: "×10¹⁵" },
  //   { value: 1e18, symbol: "×10¹⁸" },
  // ];
  // const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/;
  // const item = lookup.findLast((item) => num >= item.value);
  // return item ? (num / item.value).toFixed(digits).replace(regexp, "").concat(item.symbol) : "0";
}

export const roundToDigits = (num: number, minDecimals: number, maxDecimals: number) =>
  num.toLocaleString("en-US", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });

export interface FormattedNumber {
  number: number;
  formattedNumber: string;
  unit: string;
}

export function convertEnvironmentalCost(number: number): FormattedNumber {
  const negative = number < 0;
  const absNumber = Math.abs(number);

  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];
  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/;
  const item = lookup.findLast((item) => absNumber >= item.value);

  const formattedNumber =
    item && item.value > 1
      ? roundToDigits(Number(String(absNumber / item.value).replace(regexp, "")), 0, 2)
      : roundToDigits(absNumber, 2, 2);

  return {
    number,
    formattedNumber: `${negative && Number(formattedNumber) !== 0 ? "-" : ""}${formattedNumber}`,
    unit: item ? item.symbol : "",
  };
}

export function formatEnvironmentalCost(number: number, withoutEuro?: boolean) {
  if (number < 0.01 && number > 0) return `<${withoutEuro ? "" : "€"}0.01`;

  const conversion = convertEnvironmentalCost(number);
  const symbol = withoutEuro ? "" : "€";

  return [symbol, conversion.formattedNumber, conversion.unit].join("");
}

export interface Diff {
  number: number;
  formattedNumber: string;
  sign: "positive" | "negative" | "neutral";
}

export function formatPercentageDiff(originalNumber: number, compareNumber: number): Diff {
  const number = (originalNumber - compareNumber) / compareNumber;

  if (number < 0.01 && number > -0.01) return { formattedNumber: `≈0%`, number, sign: "neutral" };

  const conversion = formatPercentage(number);

  return {
    number,
    formattedNumber: [number > 0 ? "+" : "", conversion].join(""),
    sign: number < 0 ? "negative" : number > 0 ? "positive" : "neutral",
  };
}

export function formatEnvironmentalCostDiff(number: number): Diff {
  if (number < 0.01 && number > -0.01) return { formattedNumber: `≈€0`, number, sign: "neutral" };

  const conversion = convertEnvironmentalCost(number);

  return {
    number,
    formattedNumber: [number > 0 ? "+" : "", "€", conversion.formattedNumber, conversion.unit].join(
      ""
    ),
    sign: number < 0 ? "negative" : number > 0 ? "positive" : "neutral",
  };
}

export function convertMass(number: number): FormattedNumber {
  const negative = number < 0;
  const absNumber = Math.abs(number);

  const lookup = [
    { value: 1e-6, unit: "mg" },
    { value: 1e-3, unit: "g" },
    { value: 1, unit: "kg" },
    { value: 1e3, unit: "tonne" },
    { value: 1e6, unit: "kt" },
    { value: 1e9, unit: "Mt" },
    { value: 1e12, unit: "Gt" },
    { value: 1e15, unit: "Tt" },
  ];

  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/;
  const item = lookup.findLast((item) => absNumber >= item.value);

  return {
    number,
    unit: item ? item.unit : "kg",
    formattedNumber: item
      ? `${negative ? "-" : ""}${roundToDigits(absNumber / item.value, 0, 2).replace(regexp, "")}`
      : "0",
  };
}

export function formatCarbonFootprint(number: number, excludeCo2e?: boolean) {
  const symbol = excludeCo2e ? "" : "CO₂-Eq";
  const conversion = convertMass(number);

  return [conversion.formattedNumber, conversion.unit, symbol].join(" ");
}

export function formatCarbonFootprintDiff(number: number): Diff {
  const conversion = convertMass(number);

  return {
    number,
    formattedNumber: [(number > 0 ? "+" : "") + conversion.formattedNumber, conversion.unit].join(
      " "
    ),
    sign: number < 0 ? "negative" : number > 0 ? "positive" : "neutral",
  };
}

export function formatAmountWithUnit(number: number, unit: string, digits?: number) {
  if (digits === undefined) digits = 2;
  if (number === 0) return `0 ${unit}`;
  if (Math.abs(number) > 9999 || Math.abs(number) < 0.01)
    return `${number.toExponential(2)} ${unit}`;
  return `${roundToDigits(number, 0, 2)} ${unit}`;
}

export function formatPercentage(number: number) {
  if (isNaN(number)) return "-";

  return (
    _.round(number * 100, 2).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + "%"
  );
}

export function formatCarbonFootprintLabel(value: string) {
  const mapper = [
    { key: "carbon_footprint", label: "Carbon footprint" },
    { key: "carbon_footprint_scope_1", label: "Scope 1" },
    { key: "carbon_footprint_scope_2", label: "Scope 2" },
    { key: "carbon_footprint_scope_3", label: "Scope 3" },
  ];

  const match = mapper.find((i) => i.key === value);

  return match ? match.label : value;
}

export function formatLifecycleLabel(value: string) {
  const mapper = [
    { key: "eol", label: "End of life" },
    { key: "material", label: "Raw material" },
    { key: "production", label: "Production" },
    { key: "transport", label: "Transport" },
    { key: "usage", label: "Usage" },
    { key: "packaging", label: "Packaging" },
  ];

  const match = mapper.find((i) => i.key === value);

  return match ? match.label : value;
}

export function formatTransportLabel(value: string) {
  const mapper = [
    { key: "LAND", label: "Land" },
    { key: "AIR", label: "Air" },
    { key: "SEA", label: "Sea" },
    { key: "LAND_COMMERCIAL_VEHICLE", label: "Commercial vehicle" },
    { key: "LAND_TRAIN", label: "Train" },
    { key: "LAND_TRUCK", label: "Truck" },
  ];

  const match = mapper.find((i) => i.key === value);

  return match ? match.label : value;
}

export function formatUtilityLabel(value: string) {
  const mapper = [
    { key: "electricity", label: "Electricity" },
    { key: "water", label: "Water" },
    { key: "cooling", label: "Cooling" },
    { key: "heat", label: "Heat" },
    { key: "residual_waste", label: "Residual waste" },
    { key: "capital_goods", label: "Capital good" },
    { key: "consumable", label: "Consumable" },
  ];

  const match = mapper.find((i) => i.key === value);

  return match ? match.label : value;
}

export const formatScientific = (unit: string) => {
  return (value: number, excludeUnit: boolean = true) => {
    if (excludeUnit) {
      return formatNumberScientific(value, 2);
    }
    return formatNumberScientific(value, 2) + " " + unit;
  };
};

export const formatMass = (number: number) => {
  const converted = convertMass(number);
  return `${converted.formattedNumber} ${converted.unit}`;
};

export const formatMassWithUnit = (unit?: string) => {
  return (value: number, excludeUnit: boolean = true) => {
    const conversion = convertMass(value);
    if (excludeUnit) {
      return [conversion.formattedNumber, conversion.unit].filter((x) => x).join(" ");
    }
    return [conversion.formattedNumber, conversion.unit, unit].filter((x) => x).join(" ");
  };
};

export const formatSupplier = (supplierName: string | null, supplierAddress: string | null) => {
  return [supplierName, supplierAddress].filter(Boolean).join(" - ");
};

export const formatPackagingCategories = (categories: string[]) => {
  const packagingMap: Record<string, string> = {
    product_packaging: "Product",
    logistics_packaging: "Logistics",
  };

  return categories.map((item) => packagingMap[item]).join(", ");
};
